import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sessionsApi } from '@api/endpoints/sessions'
import { useSessionStore } from '../stores/sessionStore'
import { sessionKeys } from './useSession'
import {
  CastVoteRequest,
  VotingProgressDto,
  VotingResolutionResultDto,
  SessionTransitionResultDto,
} from '@api/types/session'

const BASE_POLL_INTERVAL = 3000 // 3 seconds base
const MAX_POLL_INTERVAL = 30000 // 30 seconds max backoff
const BACKOFF_MULTIPLIER = 2

/**
 * Hook for group session voting functionality
 */
export function useSessionVoting(sessionId: string | null) {
  const queryClient = useQueryClient()
  const store = useSessionStore()
  const [pollInterval, setPollInterval] = useState(BASE_POLL_INTERVAL)
  const [lastResolution, setLastResolution] = useState<VotingResolutionResultDto | null>(null)
  const [lastTransitionResult, setLastTransitionResult] = useState<SessionTransitionResultDto | null>(null)

  const voting = store.voting
  const roundId = voting?.roundId

  // Handle 429 backoff
  const handleRateLimitError = useCallback(() => {
    const newInterval = Math.min(pollInterval * BACKOFF_MULTIPLIER, MAX_POLL_INTERVAL)
    console.warn(`[SessionVoting] Rate limited (429). Backing off to ${newInterval / 1000}s`)
    setPollInterval(newInterval)
  }, [pollInterval])

  // Reset interval on success
  const handleSuccess = useCallback(() => {
    if (pollInterval > BASE_POLL_INTERVAL) {
      console.log('[SessionVoting] Request succeeded, resetting to base interval')
      setPollInterval(BASE_POLL_INTERVAL)
    }
  }, [pollInterval])

  // Common handler for CastVoteResultDto responses (used by both castVote and forceResolve)
  const handleVoteResult = useCallback((result: any) => {
    // Unwrap ApiResponse if needed
    const data = result?.data ?? result

    // Update user's vote in store
    if (data.vote) {
      store.setVoting(store.voting ? {
        ...store.voting,
        userVote: data.vote.voteValue,
      } : null)
    }

    // Handle resolution
    if (data.resolution) {
      setLastResolution(data.resolution)

      if (data.resolution.isResolved && store.voting) {
        store.setVoting({
          ...store.voting,
          status: 'Resolved',
          userVote: data.vote?.voteValue ?? store.voting.userVote,
        })
      }
    }

    // Handle session transition (vote resolved and session advanced to new node)
    if (data.transitionResult?.success) {
      setLastTransitionResult(data.transitionResult)
      store.loadSession(data.transitionResult.session)

      // Add new journal entries
      if (data.transitionResult.newJournalEntries?.length) {
        store.addJournalEntries(data.transitionResult.newJournalEntries)
      }
    }

    // Refresh related queries
    if (sessionId) {
      if (roundId) {
        queryClient.invalidateQueries({ queryKey: sessionKeys.voting(sessionId, roundId) })
      }
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.journal(sessionId) })
    }
  }, [sessionId, roundId, store, queryClient])

  // Poll voting progress
  const progressQuery = useQuery({
    queryKey: sessionKeys.voting(sessionId!, roundId!),
    queryFn: async () => {
      try {
        const result = await sessionsApi.getVotingProgress(sessionId!, roundId!)
        // Update store with progress
        if (result) {
          store.updateVotingProgress(result.votesCast, result.percentageVoted)
        }
        handleSuccess()
        return result as VotingProgressDto
      } catch (error: any) {
        const status = error?.response?.status || error?.status
        if (status === 429) {
          handleRateLimitError()
        }
        throw error
      }
    },
    enabled: !!sessionId && !!roundId && voting?.status === 'Open',
    refetchInterval: pollInterval,
    refetchIntervalInBackground: false,
    retry: (failureCount, error: any) => {
      const status = error?.response?.status || error?.status
      // Don't retry on 429, we'll just wait for the next interval
      if (status === 429) return false
      return failureCount < 3
    },
  })

  // Cast vote mutation
  const castVoteMutation = useMutation({
    mutationFn: async (data: CastVoteRequest) => {
      if (!sessionId) throw new Error('No active session')
      const result = await sessionsApi.castVote(sessionId, data)
      return result
    },
    onSuccess: (result) => {
      handleVoteResult(result)
    },
  })

  // Force-resolve voting mutation (admin/moderator)
  const forceResolveMutation = useMutation({
    mutationFn: async () => {
      if (!sessionId || !roundId) throw new Error('No active voting round')
      const result = await sessionsApi.forceResolveVoting(sessionId, roundId)
      return result
    },
    onSuccess: (result) => {
      handleVoteResult(result)
    },
  })

  // Convenience method
  const castVote = (voteValue: string) => {
    if (!roundId) {
      console.error('No active voting round')
      return
    }
    castVoteMutation.mutate({ votingRoundId: roundId, voteValue })
  }

  const forceResolve = () => {
    if (!roundId) {
      console.error('No active voting round')
      return
    }
    forceResolveMutation.mutate()
  }

  // Check if user has voted
  const hasVoted = voting?.userVote !== null

  // Calculate time remaining
  const getTimeRemaining = (): number | null => {
    if (!voting?.timerExpiresAt) return null
    const expiresAt = new Date(voting.timerExpiresAt).getTime()
    const now = Date.now()
    const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000))
    return remaining
  }

  return {
    // Voting state
    voting,
    isVotingActive: voting?.status === 'Open',
    hasVoted,
    userVote: voting?.userVote,
    options: voting?.options || [],

    // Progress
    progress: progressQuery.data,
    votesCast: voting?.votesCast || 0,
    totalVoters: voting?.totalVoters || 0,
    requiredVotes: voting?.requiredVotes || 0,
    percentageVoted: voting?.percentageVoted || 0,
    getTimeRemaining,

    // Cast vote
    castVote,
    isVoting: castVoteMutation.isPending,
    voteError: castVoteMutation.error,

    // Force resolve (admin/moderator)
    forceResolve,
    isForceResolving: forceResolveMutation.isPending,
    forceResolveError: forceResolveMutation.error,

    // Resolution & transition results (for UI feedback)
    resolution: lastResolution,
    transitionResult: lastTransitionResult,

    // Loading states
    isLoadingProgress: progressQuery.isLoading,
  }
}
